/**
 * Alert — inline message block (PRD §10.2 colour language).
 *
 * Soft fill plus a 4px accent on the reading edge, so the tone is legible from
 * the far side of a dense screen without a solid coloured panel taking it over.
 */

import { forwardRef, useState } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XOctagon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

type AlertTone = 'info' | 'success' | 'warning' | 'danger';

interface ToneSpec {
  /** `border-s-*` colour is listed after the fill so tailwind-merge keeps it. */
  className: string;
  iconClassName: string;
  icon: LucideIcon;
}

const TONE_SPEC: Record<AlertTone, ToneSpec> = {
  info: {
    className: 'bg-blue-50 text-blue-900 border-s-blue-500',
    iconClassName: 'text-blue-600',
    icon: Info,
  },
  success: {
    className: 'bg-emerald-50 text-emerald-900 border-s-emerald-500',
    iconClassName: 'text-emerald-600',
    icon: CheckCircle2,
  },
  warning: {
    className: 'bg-amber-50 text-amber-900 border-s-amber-500',
    iconClassName: 'text-amber-600',
    icon: AlertTriangle,
  },
  danger: {
    className: 'bg-red-50 text-red-900 border-s-red-500',
    iconClassName: 'text-red-600',
    icon: XOctagon,
  },
};

/** `title` is the alert's own headline, so the native tooltip attribute is off the table. */
interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: AlertTone;
  /** Arabic headline; the body reads as a paragraph without it. */
  title?: ReactNode;
  /** Overrides the tone's default Lucide icon. Pass `null` to drop it entirely. */
  icon?: LucideIcon | null;
  dismissible?: boolean;
  /** Fired after the alert removes itself; also lets the caller unmount it. */
  onDismiss?: () => void;
  /** Arabic tooltip on the close control. */
  dismissLabel?: string;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    tone = 'info',
    title,
    icon,
    dismissible = false,
    onDismiss,
    dismissLabel: dismissLabelProp,
    className,
    children,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const dismissLabel = dismissLabelProp ?? t('actions.close');
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const spec = TONE_SPEC[tone];
  const Icon = icon === null ? null : (icon ?? spec.icon);

  return (
    <div
      ref={ref}
      // A warning or an error interrupts; an info line is only announced when the
      // screen reader gets to it.
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex items-start gap-2 rounded border-s-4 p-2.5 text-xs', spec.className, className)}
      {...rest}
    >
      {Icon ? <Icon className={cn('mt-px h-4 w-4 shrink-0', spec.iconClassName)} aria-hidden="true" /> : null}

      <div className="min-w-0 flex-1">
        {title ? <div className="mb-0.5 font-bold">{title}</div> : null}
        {children ? <div className="leading-relaxed">{children}</div> : null}
      </div>

      {dismissible ? (
        <button
          type="button"
          title={dismissLabel}
          aria-label={dismissLabel}
          onClick={() => {
            setDismissed(true);
            onDismiss?.();
          }}
          className="-me-1 shrink-0 rounded p-0.5 opacity-60 transition hover:bg-black/5 hover:opacity-100"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
});
